import { Component, ViewEncapsulation, Input, Output, EventEmitter, ElementRef, ViewChild, TemplateRef, ContentChild, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { ExtendedBathDaiModel } from '@core/models/models/bathe.model';
import { BATHE_MANAGEMENT, OfficeType } from '@core/constants';
import { BatheHelper } from '@modules/bathe-management/bathe.helper';
import { Utils } from '@core/helper/utils.helper';
import { AvatarDealerService, AvatarStatus } from '@core/services/common';
import { Subscription } from 'rxjs';

export interface DragDropTableColumn {
    header: string;
    field: string;
    width?: string;
    // Minimum px width applied only in fit-to-screen mode (no horizontal scroll), so
    // percentage columns stay readable on small screens / high zoom instead of squishing.
    minWidth?: string;
}

export interface ProcessedRowData {
    originalData: any;
    cells: Map<string, any>;
    avatarUrl?: string;
    genderClass?: string;
    lastBatheDateFormatted?: string;
    lastBatheDaysAgo?: string;
    isShortTerm?: boolean;
    bathWeeksText?: string;
    bathDaysText?: string;
    daiCd?: string;
    avatarStatus?: AvatarStatus;
}

@Component({
    selector: 'caremoni-drag-and-drop-table',
    templateUrl: './drag-and-drop-table.component.html',
    styleUrl: './drag-and-drop-table.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DragAndDropTableComponent implements OnInit, OnChanges, OnDestroy {
    // Extra Row Constants
    private readonly EXTRA_ROW_HEIGHT = 370;
    private readonly EXTRA_ROW_SCROLL_PADDING = 20;
    private readonly SCROLL_ANIMATION_DELAY = 300;
    private readonly SCROLL_RECALC_DELAY = 350;
    private readonly SCROLL_COMPLETE_DELAY = 500;
    private readonly EXTRA_ROW_CLOSE_DELAY = 200;

    // Gender Constants
    private readonly MALE_GENDER_VALUE = 1;
    private readonly FEMALE_GENDER_VALUE = 2;
    private readonly GENDER_MALE_LABEL = '男';
    private readonly GENDER_FEMALE_LABEL = '女';


    // Timing Constants
    private readonly EVENT_LISTENER_INIT_DELAY = 100;
    private readonly SCROLLBAR_WIDTH_OFFSET = 5;

    // Priority CSS Class Mapping
    private readonly PRIORITY_CLASS_MAP: Readonly<{ [key: string]: string }> = {
        '最初': 'priority-badge--first',
        '優先': 'priority-badge--priority',
        '最後': 'priority-badge--last',
        '1': 'priority-badge--first',
        '2': 'priority-badge--priority',
        '3': 'priority-badge--last'
    };

    // Bathe Method Display Mapping
    private readonly BATHE_METHOD_MAP: Readonly<{ [key: string]: string }> = {
        '普通浴': '普通浴',
        '個浴': '個浴',
        'チェアー浴': 'チェアー浴',
        'リフト浴': 'リフト浴',
        'ストレッチャー浴': 'ストレッチャー浴',
        '臥床浴': '臥床浴',
        'シャワー浴': 'シャワー浴',
        '1': '普通浴',
        '2': '個浴',
        '3': 'チェアー浴',
        '4': 'リフト浴',
        '5': 'ストレッチャー浴',
        '6': '臥床浴',
        '7': 'シャワー浴'
    };

    TEXT = BATHE_MANAGEMENT

    @Input() columns: DragDropTableColumn[] = [];
    @Input() darkMode: boolean = true;
    @Input() data: any[] = [];
    @Input() tableClass = 'table table-sm table-bordered table-striped text-center';
    @Input() extraRowTemplate?: TemplateRef<any>;
    @Input() enableAutoSort = false; // Enable automatic sorting by priorityCd
    @Input() enableDragDrop = true; // Enable/disable drag and drop functionality
    @Input() enableRowClick = true; // Enable/disable row click to open edit extra row
    @Input() showDragHandle = true; // Show/hide drag handle column
    @Input() showEditColumn = false; // Show/hide edit icon column
    @Input() enablePriorityBadge = false; // Enable/disable priority badge styling
    @Input() officeType: OfficeType = OfficeType.newTsusho; // Office type for short-term user detection
    @Input() enableBatheTimeHint = false;  // Enable/disable bathe time hint display
    @Input() enableHorizontalScroll = false; // Allow non-pinned columns to scroll horizontally (tablet UX)
    @Input() stickyLeadingColumns = 0; // Number of leading content columns to pin (the drag handle pins automatically)
    // When true, a row click emits rowEditByDialog instead of opening the inline extra
    // row — used on mobile/tablet where the inline editor + horizontal scroll hurts UX.
    @Input() editViaDialog = false;

    // Drag handle column width — keep in sync with $drag-handle-width in the SCSS
    private readonly DRAG_HANDLE_WIDTH = 40;

    @Output() dataChange = new EventEmitter<any[]>();
    @Output() orderChanged = new EventEmitter<any[]>();
    @Output() externalItemDropped = new EventEmitter<any>();
    @Output() extraRowScrolling = new EventEmitter<boolean>();
    @Output() extraRowOpened = new EventEmitter<any>();
    @Output() extraRowClosed = new EventEmitter<void>();
    @Output() rowEditByDialog = new EventEmitter<any>();

    @ViewChild('tableBodyWrapper', { static: false }) tableBodyWrapper!: ElementRef;
    @ContentChild('extraRowContent') extraRowContentTemplate?: TemplateRef<any>;

    /**
     * The element that actually scrolls the rows. Exposed so a host can observe scroll
     * state (e.g. to show a "more rows below" affordance) without reaching into the DOM
     * with a global query, which would pick the wrong table when several are rendered.
     */
    get scrollContainer(): HTMLElement | null {
        return this.tableBodyWrapper?.nativeElement ?? null;
    }

    draggedItem: any | null = null;
    isDragEnabled = false;
    private dragImageEl: HTMLElement | null = null;
    selectedRow: any | null = null;
    extraRowTop = 0;
    extraRowLeft = 0;
    hoveredRow: any | null = null;
    isExtraRowClosing = false;
    isExtraRowOpen = false; // Track if extra row is currently open

    // Blocking overlay positions (split to avoid covering selected row)
    overlayTopHeight = 0;
    overlayBottomTop = 0;
    overlayBottomHeight = 0;

    // Dropzone state
    showDropzone = false;
    dropzoneTop = 0;
    dropzoneHeight = 0;
    dropzoneLeft = 0;
    dropzoneWidth = '100%';
    private isDraggingFromExternal = false;
    isNyusho: boolean = true;


    // Processed data to avoid function calls in template
    processedData: ProcessedRowData[] = [];
    private avatarSubscriptions: Subscription[] = [];

    constructor(private avatarDealerService: AvatarDealerService) { }

    ngOnInit() {
        this.processData();
        this.setupDropzoneListeners();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] || changes['columns']) {
            this.processData();
        }

        if (changes['officeType'] || changes['officeType']) {
            this.isNyusho = Utils.isNyusho(this.officeType);
        }
    }

    ngOnDestroy() {
        this.clearAvatarSubscriptions();
        this.removeDropzoneListeners();
    }

    /**
     * Process raw data into optimized format for rendering
     * Pre-computes values to avoid repeated calculations in templates
     */
    private processData(): void {
        this.clearAvatarSubscriptions();
        const sortedData = this.enableAutoSort ? this.sortByPriority(this.data) : this.data;
        this.processedData = sortedData.map(item => this.processRowData(item));
        this.bindAvatarUpdates();
    }

    /**
     * Process a single row of data
     */
    private processRowData(item: any): ProcessedRowData {
        const cells = new Map<string, any>();

        // Pre-compute all cell values
        this.columns.forEach(column => {
            cells.set(column.field, this.getCellValue(item, column.field));
        });

        // Determine gender and avatar
        const isFemale = item.gender === this.GENDER_FEMALE_LABEL;
        const avatarUrl = this.avatarDealerService.getAvatarUrl(item.daiCd)
            || item.avatar
            || this.getDefaultAvatar(isFemale ? this.FEMALE_GENDER_VALUE : this.MALE_GENDER_VALUE);
        const genderClass = isFemale ? 'avatar--female' : (item.gender === this.GENDER_MALE_LABEL ? 'avatar--male' : '');
        const shortTermUser = this.isShortTermUser(item);
        // Pre-compute last bathe date information
        const { lastBatheDateFormatted, lastBatheDaysAgo } = this.calculateLastBatheInfo(item.lastBatheDate);

        let bathWeeksText = BatheHelper.formatBathWeeks(item.bathWeeks);
        let bathDaysText = BatheHelper.getBasicBatheDate(item.bathDays);
        if(!this.isNyusho) {
            if (!Utils.isNullOrEmpty(bathWeeksText) && Utils.isNullOrEmpty(bathDaysText)) {
                bathWeeksText = "";
            }
            // Edge case: If only bathDays has value and bathWeeks is all zeros, show "毎日"
            else if (Utils.isNullOrEmpty(bathWeeksText) && !Utils.isNullOrEmpty(bathDaysText)) {
                if(Utils.isNullOrEmpty(bathDaysText) || item.bathWeeks === '00000') {
                    bathWeeksText = "毎週";
                }
            }
        }

        return {
            originalData: item,
            cells,
            avatarUrl,
            genderClass,
            lastBatheDateFormatted,
            lastBatheDaysAgo,
            isShortTerm: shortTermUser,
            bathWeeksText,
            bathDaysText,
            daiCd: item.daiCd,
            // If we already have a cached avatar it is resolved; otherwise leave
            // undefined until the dealer reports a status (avoids a spinner flash
            // for rows that never requested an avatar).
            avatarStatus: this.avatarDealerService.getAvatarUrl(item.daiCd) ? 'resolved' : undefined
        };
    }

    isShortTermUser(item: ExtendedBathDaiModel): boolean {
        return BatheHelper.isShortTermUser(item, this.officeType);
    }

    /**
     * Calculate formatted last bathe date and days ago text
     */
    private calculateLastBatheInfo(lastBatheDate: string | undefined): { lastBatheDateFormatted: string; lastBatheDaysAgo: string } {
        return BatheHelper.calculateLastBatheInfo(lastBatheDate);
    }

    /**
     * Sort data by priorityCd (BatheType enum values)
     * Priority order: First (1) > Priority (2) > None (3) > Last (4)
     * Items are grouped by priority and sorted by custom order
     */
    private sortByPriority(data: any[]): any[] {
        return BatheHelper.sortByPriority(data);
    }

    /** A resolved avatar URL failed to load (e.g. its S3 object is gone): fall
     *  back to the default image and ask the dealer to invalidate + re-fetch. */
    onAvatarError(row: ProcessedRowData): void {
        const isFemale = row.originalData?.gender === this.GENDER_FEMALE_LABEL;
        const def = this.getDefaultAvatar(isFemale ? this.FEMALE_GENDER_VALUE : this.MALE_GENDER_VALUE);
        if (row.avatarUrl === def) return; // default itself failed — stop
        row.avatarUrl = def;
        if (row.daiCd) this.avatarDealerService.retryFailedUrl(row.daiCd);
    }

    getDefaultAvatar(gender: number): string {
        if (gender === this.FEMALE_GENDER_VALUE) {
            return "assets/images/icons-customer-femal.png";
        }
        return "assets/images/icons-customer-male.png";
    }

    private bindAvatarUpdates(): void {
        this.processedData.forEach(processedRow => {
            if (!processedRow.daiCd) return;

            const subscription = this.avatarDealerService.avatarUrl$(processedRow.daiCd).subscribe(avatarUrl => {
                if (!avatarUrl) return;

                processedRow.avatarUrl = avatarUrl;
                processedRow.originalData.avatar = avatarUrl;
            });
            this.avatarSubscriptions.push(subscription);

            // Drive the loading spinner / terminal state from the dealer status.
            const statusSub = this.avatarDealerService.status$(processedRow.daiCd).subscribe(status => {
                processedRow.avatarStatus = status;
            });
            this.avatarSubscriptions.push(statusSub);
        });
    }

    private clearAvatarSubscriptions(): void {
        this.avatarSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.avatarSubscriptions = [];
    }

    trackByDaiCd(_: number, row: ProcessedRowData): string | undefined {
        return row.daiCd;
    }

    trackByColumnField(_: number, column: DragDropTableColumn): string {
        return column.field;
    }

    enableDrag() {
        // Don't enable drag if overlay is active
        if (this.isExtraRowOpen) return;
        this.isDragEnabled = true;
    }

    disableDrag() {
        this.isDragEnabled = false;
    }

    onDragStart(event: DragEvent, processedRow: any) {
        // Block drag if overlay is active
        if (this.isExtraRowOpen) return;

        this.draggedItem = processedRow?.originalData;
        // Hide dropzone when dragging internally
        this.showDropzone = false;
        this.isDraggingFromExternal = false;
        // The browser's default drag image of a <tr> renders tiny/black on WebKit
        // (and worse with sticky cells), so use a clean custom preview.
        this.setCustomDragImage(event, processedRow);
    }

    onDragEnd() {
        this.draggedItem = null;
        this.hoveredRow = null;
        this.isDragEnabled = false;
        this.removeDragImage();
    }

    /**
     * Build the drag preview shown under the pointer.
     *
     * Touch devices (iPad) cap the custom drag image and scale an oversized
     * bitmap down, so a full-width row would render shrunken/squeezed there.
     * We therefore use a compact avatar+name preview on touch (small enough to
     * render 1:1) and the full-row clone on desktop (where there's no such cap
     * and the native <tr> image renders tiny/black with sticky cells).
     */
    private setCustomDragImage(event: DragEvent, processedRow: any): void {
        if (!event?.dataTransfer) return;

        // Clear any stale ghost left over from a previous drag (iOS dragend is flaky,
        // so onDragEnd cleanup can be missed and a leftover would overlap this one).
        document.querySelectorAll('.dnd-drag-ghost-el').forEach(el => el.remove());

        const ghost = this.isTouchDevice()
            ? this.buildCompactDragGhost(processedRow)
            : this.buildFullRowDragGhost(event);
        if (!ghost) return;

        ghost.classList.add('dnd-drag-ghost-el');
        document.body.appendChild(ghost);
        this.dragImageEl = ghost;

        // Anchor the preview under the finger/cursor.
        event.dataTransfer.setDragImage(ghost, 24, ghost.offsetHeight / 2);
    }

    /**
     * Tooltip trigger: hover on desktop, focus on touch. On touch the synthetic
     * hover events fire-and-clear around a tap, which makes a hover tooltip flash
     * and hide; focus (paired with a click-to-focus on the icon) stays open.
     */
    get tooltipShowEvent(): 'hover' | 'focus' {
        return this.isTouchDevice() ? 'focus' : 'hover';
    }

    // Whether the tapped tooltip icon was already focused before this tap started.
    private tooltipWasFocused = false;

    /** Capture focus state before the browser moves focus on pointer-down. */
    onTooltipIconPointerDown(event: Event): void {
        this.tooltipWasFocused = document.activeElement === event.currentTarget;
    }

    /**
     * Touch toggle for the tooltip icon: opening a closed tooltip focuses it,
     * re-tapping the open icon blurs it (closes). Tapping elsewhere closes via blur.
     */
    onTooltipIconTap(event: Event): void {
        const el = event.currentTarget as HTMLElement;
        if (this.tooltipWasFocused) {
            el.blur();
        } else {
            el.focus();
        }
    }

    /**
     * PrimeNG tries right → left → top → bottom and stops at the first side that
     * fits, but the last fallback is applied even when it still overflows. On a
     * small tablet (iPad mini) no side fits the wide hint, so pull the box back
     * inside the viewport after it has been positioned.
     */
    onTooltipIconShow(): void {
        requestAnimationFrame(() => {
            const tooltip = document.querySelector('.p-tooltip.right-expand') as HTMLElement | null;
            if (!tooltip) return;

            const margin = 8;
            const rect = tooltip.getBoundingClientRect();
            const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
            const maxTop = Math.max(margin, window.innerHeight - rect.height - margin);
            const left = Math.min(Math.max(rect.left, margin), maxLeft);
            const top = Math.min(Math.max(rect.top, margin), maxTop);

            // PrimeNG writes page coordinates, so re-add the scroll offset it included.
            if (left !== rect.left) tooltip.style.left = `${left + window.scrollX}px`;
            if (top !== rect.top) tooltip.style.top = `${top + window.scrollY}px`;
        });
    }

    /** True when the primary input is touch (e.g. iPad), which caps drag-image size. */
    private isTouchDevice(): boolean {
        return (typeof window !== 'undefined' && 'ontouchstart' in window)
            || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
    }

    /**
     * Compact avatar+name preview — fits within the touch drag-image cap.
     * Styled like the desktop row preview (square corners + light border). Square
     * corners keep the bounding box fully opaque, so iPad (which rasterises the
     * drag image onto a black backing) has no transparent pixels to render black.
     */
    private buildCompactDragGhost(processedRow: any): HTMLElement {
        const name = processedRow?.cells?.get?.('name') ?? '';
        const avatarUrl = processedRow?.avatarUrl ?? '';

        const ghost = document.createElement('div');
        ghost.className = 'dnd-drag-ghost-compact' + (this.darkMode ? ' dark-mode' : '');

        if (avatarUrl) {
            const img = document.createElement('img');
            img.src = avatarUrl;
            img.className = 'dnd-drag-ghost-compact__avatar';
            ghost.appendChild(img);
        }

        const label = document.createElement('span');
        label.className = 'dnd-drag-ghost-compact__name';
        label.textContent = name; // textContent avoids HTML injection from user names
        ghost.appendChild(label);

        return ghost;
    }

    /**
     * Full-row drag preview by cloning the dragged <tr>, clipped to the visible
     * width. Used on desktop, where the native <tr> drag image renders tiny/black
     * (especially with sticky cells) but there's no drag-image size cap.
     */
    private buildFullRowDragGhost(event: DragEvent): HTMLElement | null {
        const row = (event.target as HTMLElement)?.closest('tr');
        if (!row) return null;

        const rect = row.getBoundingClientRect();

        // Clip to the visible width so off-screen (horizontally-scrolled) columns
        // don't make the image wider than the viewport.
        const visibleWidth = this.tableBodyWrapper?.nativeElement?.clientWidth ?? rect.width;
        const ghostWidth = Math.min(rect.width, visibleWidth);

        const ghost = document.createElement('div');
        ghost.className = `dnd-drag-ghost${this.darkMode ? ' dark-mode' : ''}`;
        ghost.style.width = `${ghostWidth}px`;

        // Cloned row wrapped in its own table so it lays out as a real row at full width.
        const table = document.createElement('table');
        table.className = `${this.tableClass} dnd-drag-ghost-table${this.darkMode ? ' dark-mode' : ''}`;
        table.style.width = `${rect.width}px`;

        const tbody = document.createElement('tbody');
        const clonedRow = row.cloneNode(true) as HTMLElement;

        // Strip sticky positioning so pinned cells don't stack on top of each other.
        clonedRow.querySelectorAll<HTMLElement>('.sticky-col').forEach(cell => {
            cell.style.position = 'static';
            cell.style.left = 'auto';
        });

        tbody.appendChild(clonedRow);
        table.appendChild(tbody);
        ghost.appendChild(table);
        return ghost;
    }

    private removeDragImage(): void {
        this.dragImageEl?.remove();
        this.dragImageEl = null;
    }

    onDrop() {
        if (this.draggedItem) {
            const draggedIndex = this.data.indexOf(this.draggedItem);
            this.data.splice(draggedIndex, 1);
            this.data.splice(draggedIndex, 0, this.draggedItem);
            this.draggedItem = null;
        }
    }

    onDropItem(event: any, droppedItem: any) {
        if (this.draggedItem && this.draggedItem !== droppedItem) {
            // Internal reorder (dragging within table)
            const draggedIndex = this.data.indexOf(this.draggedItem);
            const droppedIndex = this.data.indexOf(droppedItem);

            this.data.splice(draggedIndex, 1);
            this.data.splice(droppedIndex, 0, this.draggedItem);

            this.draggedItem = null;

            // Re-sort if auto-sort is enabled
            if (this.enableAutoSort) {
                this.data = this.sortByPriority(this.data);
            }

            // Emit events for parent component
            this.dataChange.emit(this.data);
            this.orderChanged.emit(this.data);
        } else if (!this.draggedItem) {
            // External drop (from sidebar)
            this.externalItemDropped.emit(droppedItem);
        }

        // Clear hover highlight
        this.hoveredRow = null;
    }

    onRowMouseEnter(item: any) {
        // Only highlight when dragging
        if (this.draggedItem || this.isDraggingExternal()) {
            this.hoveredRow = item;
        }
    }

    onRowMouseLeave() {
        this.hoveredRow = null;
    }

    private isDraggingExternal(): boolean {
        // Check if there's an external drag happening
        // This will be true when dragging from sidebar but draggedItem is null
        return !this.draggedItem && document.querySelector('.p-draggable-dragging') !== null;
    }

    /**
     * Set up event listeners for dropzone functionality
     */
    private setupDropzoneListeners(): void {
        setTimeout(() => {
            if (this.tableBodyWrapper?.nativeElement) {
                const element = this.tableBodyWrapper.nativeElement;
                element.addEventListener('scroll', this.updateDropzonePosition.bind(this));
                element.addEventListener('dragenter', this.handleTableDragEnter.bind(this));
                element.addEventListener('dragover', this.handleTableDragOver.bind(this));
                element.addEventListener('dragleave', this.handleTableDragLeave.bind(this));
                element.addEventListener('drop', this.handleTableDrop.bind(this));
            }

            // Add document-level dragend listener to catch all drag end events (macOS fix)
            document.addEventListener('dragend', this.handleDocumentDragEnd.bind(this));
        }, this.EVENT_LISTENER_INIT_DELAY);
    }

    private removeDropzoneListeners(): void {
        if (this.tableBodyWrapper?.nativeElement) {
            this.tableBodyWrapper.nativeElement.removeEventListener('scroll', this.updateDropzonePosition.bind(this));
            this.tableBodyWrapper.nativeElement.removeEventListener('dragenter', this.handleTableDragEnter.bind(this));
            this.tableBodyWrapper.nativeElement.removeEventListener('dragover', this.handleTableDragOver.bind(this));
            this.tableBodyWrapper.nativeElement.removeEventListener('dragleave', this.handleTableDragLeave.bind(this));
            this.tableBodyWrapper.nativeElement.removeEventListener('drop', this.handleTableDrop.bind(this));
        }
        // Remove document-level listener
        document.removeEventListener('dragend', this.handleDocumentDragEnd.bind(this));
    }

    /**
     * Handle document-level dragend event (macOS fix)
     * This catches all drag end events to ensure state is reset
     */
    private handleDocumentDragEnd(): void {
        // Use a small timeout to ensure this runs after PrimeNG's dragend
        setTimeout(() => {
            this.resetDragState();
        }, 50);
    }

    /**
     * Handle drag enter event on table
     */
    private handleTableDragEnter(event: DragEvent): void {
        if (this.isExternalDrag()) {
            this.isDraggingFromExternal = true;
            this.showDropzone = true;
            this.updateDropzonePosition();
        }
    }

    /**
     * Handle drag over event on table
     */
    private handleTableDragOver(event: DragEvent): void {
        if (this.isExternalDrag()) {
            event.preventDefault();
            this.updateDropzonePosition();
        }
    }

    /**
     * Handle drag leave event on table
     */
    private handleTableDragLeave(event: DragEvent): void {
        if (this.hasLeftTableBounds(event)) {
            this.hideDropzone();
        }
    }

    /**
     * Handle drop event on table
     */
    private handleTableDrop(): void {
        this.hideDropzone();
    }

    /**
     * Check if drag is from external source (not internal reordering)
     */
    private isExternalDrag(): boolean {
        return !this.draggedItem;
    }

    /**
     * Check if drag event has left the table bounds
     */
    private hasLeftTableBounds(event: DragEvent): boolean {
        const wrapper = this.tableBodyWrapper.nativeElement;
        const rect = wrapper.getBoundingClientRect();

        return (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
        );
    }

    /**
     * Hide the dropzone overlay
     */
    private hideDropzone(): void {
        this.showDropzone = false;
        this.isDraggingFromExternal = false;
    }

    /**
     * Update dropzone position to match visible viewport
     */
    private updateDropzonePosition(): void {
        if (!this.tableBodyWrapper?.nativeElement || !this.showDropzone) {
            return;
        }

        const wrapper = this.tableBodyWrapper.nativeElement;
        const rect = wrapper.getBoundingClientRect();

        this.dropzoneTop = wrapper.scrollTop;
        // On mobile the wrapper scrolls horizontally — keep the overlay anchored to
        // the visible area by offsetting by the current horizontal scroll position.
        this.dropzoneLeft = wrapper.scrollLeft;

        // Fill the visible body area whether or not there are rows. The empty state used
        // to get a fixed 200px, which is a small target in the middle of a tall body —
        // exactly the case where the drop zone is the only thing to aim at.
        this.dropzoneHeight = rect.height;
        this.dropzoneWidth = `${rect.width - this.SCROLLBAR_WIDTH_OFFSET}px`;
    }

    private getCellValue(item: any, field: string): any {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item);

        // Pre-process batheMethod display value (handles both batheMethod and bathMethodCd)
        if ((field === 'batheMethod' || field === 'bathMethodCd') && value !== null && value !== undefined) {
            return this.getBatheMethodDisplay(value);
        }

        // Pre-process priority display value (already handled by getPriorityClass in template)
        // No need to convert here as it's just text display

        return value;
    }

    /**
     * Handle row click to open/close extra row
     */
    onRowClick(event: MouseEvent, item: any): void {
        if (!this.enableRowClick) return;

        // Mobile/tablet: edit via a dialog instead of the inline extra row.
        if (this.editViaDialog) {
            this.rowEditByDialog.emit(item);
            return;
        }

        // If overlay is active (extra row open), block all interactions
        if (this.isExtraRowOpen && this.selectedRow !== item) {
            return;
        }

        // If clicking the same row, close it
        if (this.selectedRow === item) {
            this.closeExtraRow();
            this.extraRowClosed.emit(); // Emit close event
            return;
        }

        this.selectedRow = item;
        this.isExtraRowOpen = true;
        this.extraRowOpened.emit(item);

        const rowElement = event.currentTarget as HTMLElement;
        this.positionExtraRow(rowElement);
    }

    /**
     * Handle edit icon click to emit edit event
     */
    onEditIconClick(event: MouseEvent, item: any): void {
        event.stopPropagation(); // Prevent row click event
        this.rowEditByDialog.emit(item);
    }

    /**
     * Position the extra row below the clicked row and calculate overlay positions
     */
    private positionExtraRow(rowElement: HTMLElement): void {
        const rect = rowElement.getBoundingClientRect();
        const wrapperElement = this.tableBodyWrapper.nativeElement;
        const wrapperRect = wrapperElement.getBoundingClientRect();

        this.extraRowLeft = 0;
        this.extraRowTop = rect.bottom - wrapperRect.top + wrapperElement.scrollTop;

        // Calculate blocking overlay positions to avoid covering selected row
        this.calculateOverlayPositions(rowElement, wrapperElement, wrapperRect);

        const spaceBelow = wrapperRect.bottom - rect.bottom;

        if (spaceBelow < this.EXTRA_ROW_HEIGHT) {
            this.scrollToFitExtraRow(rowElement, rect, wrapperElement, wrapperRect);
        }
    }

    /**
     * Calculate overlay positions to split around the selected row
     */
    private calculateOverlayPositions(
        rowElement: HTMLElement,
        wrapperElement: HTMLElement,
        wrapperRect: DOMRect
    ): void {
        const rect = rowElement.getBoundingClientRect();

        // Top overlay: from top of wrapper to top of selected row
        const rowTopRelativeToWrapper = rect.top - wrapperRect.top + wrapperElement.scrollTop;
        this.overlayTopHeight = rowTopRelativeToWrapper;

        // Bottom overlay: from bottom of selected row to bottom of wrapper
        const rowBottomRelativeToWrapper = rect.bottom - wrapperRect.top + wrapperElement.scrollTop;
        this.overlayBottomTop = rowBottomRelativeToWrapper;
        this.overlayBottomHeight = wrapperElement.scrollHeight - rowBottomRelativeToWrapper;
    }

    /**
     * Scroll the table to fit the extra row in viewport
     */
    private scrollToFitExtraRow(
        rowElement: HTMLElement,
        rect: DOMRect,
        wrapperElement: HTMLElement,
        wrapperRect: DOMRect
    ): void {
        this.extraRowScrolling.emit(true);

        setTimeout(() => {
            const scrollAmount = (rect.bottom + this.EXTRA_ROW_HEIGHT) - wrapperRect.bottom;
            wrapperElement.scrollTo({
                top: wrapperElement.scrollTop + scrollAmount + this.EXTRA_ROW_SCROLL_PADDING,
                behavior: 'smooth'
            });

            // Recalculate positions after scroll
            setTimeout(() => {
                const updatedRect = rowElement.getBoundingClientRect();
                this.extraRowTop = updatedRect.bottom - wrapperRect.top + wrapperElement.scrollTop;

                // Recalculate overlay positions after scroll
                this.calculateOverlayPositions(rowElement, wrapperElement, wrapperRect);
            }, this.SCROLL_RECALC_DELAY);

            // Reset scrolling flag
            setTimeout(() => {
                this.extraRowScrolling.emit(false);
            }, this.SCROLL_COMPLETE_DELAY);
        }, this.SCROLL_ANIMATION_DELAY);
    }

    /**
     * Close the extra row with animation
     */
    closeExtraRow(): void {
        if (!this.selectedRow) return;

        this.isExtraRowClosing = true;

        setTimeout(() => {
            this.selectedRow = null;
            this.isExtraRowClosing = false;
            this.isExtraRowOpen = false;
        }, this.EXTRA_ROW_CLOSE_DELAY);
    }

    /**
     * Force reset all drag-related states
     * This is needed for macOS where drag events may not fire correctly after drop
     */
    resetDragState(): void {
        this.draggedItem = null;
        this.hoveredRow = null;
        this.isDragEnabled = false;
        this.showDropzone = false;
        this.isDraggingFromExternal = false;
    }

    /**
     * Get CSS class for priority badge based on priority value
     * @param priority - Priority as string (Japanese) or number (enum)
     * @returns CSS class name
     */
    getPriorityClass(priority: string | number): string {
        return this.PRIORITY_CLASS_MAP[priority?.toString()] || '';
    }

    /** Whether the drag-handle column should be pinned (sticky-left). */
    get isDragHandleSticky(): boolean {
        return this.enableHorizontalScroll && this.stickyLeadingColumns > 0 && this.showDragHandle;
    }

    /** Whether the content column at `index` should be pinned (sticky-left). */
    isStickyColumn(index: number): boolean {
        return this.enableHorizontalScroll && index < this.stickyLeadingColumns;
    }

    /**
     * Cumulative left offset (px) for a pinned content column, so each pinned
     * column sits flush against the previous one. Returns null for non-pinned
     * columns so the `left` style binding is omitted.
     */
    stickyLeftFor(index: number): number | null {
        if (!this.isStickyColumn(index)) {
            return null;
        }
        let left = this.showDragHandle ? this.DRAG_HANDLE_WIDTH : 0;
        for (let i = 0; i < index; i++) {
            left += this.parsePxWidth(this.columns[i]?.width);
        }
        return left;
    }

    /** Parse a px width string (e.g. "200px") to a number; non-px values yield 0. */
    private parsePxWidth(width?: string): number {
        if (!width) {
            return 0;
        }
        const parsed = parseInt(width, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    /**
     * Get display text for bathe method
     * @param method - Method as string (Japanese) or number (enum)
     * @returns Display text
     */
    getBatheMethodDisplay(method: string | number): string {
        return this.BATHE_METHOD_MAP[method?.toString()] || '';
    }
}
